import { merge } from 'lodash';
import { useEffect } from 'react';

import { useI18nContext, withI18n } from 'components/I18nProvider';
import Select from 'components/inputs/Select';
import { Stack } from 'components/layout';
import { Text } from 'components/typography';
import useSetState from 'utils/useSetState';

import { DatasetConflict, DatasetMode } from './DatasetOptions.types';

export interface DatasetOptionsState {
  mode: DatasetMode;
  conflict: DatasetConflict;
}

export interface DatasetOptionsProps {
  onChange: (update: DatasetOptionsState) => void;
  defaultValue?: Partial<DatasetOptionsState>;
}

const DatasetOptions = ({ onChange, defaultValue }: DatasetOptionsProps) => {
  const { tn } = useI18nContext();

  const [state, setState] = useSetState<DatasetOptionsState>(() =>
    merge(
      {
        mode: DatasetMode.CREATE_OVERWRITE,
        conflict: DatasetConflict.OVERWRITE,
      },
      defaultValue
    )
  );

  useEffect(() => {
    onChange(state);
  }, [onChange, state]);

  const modeOptions = [
    { label: tn(DatasetMode.MAP_EXISTING), value: DatasetMode.MAP_EXISTING },
    { label: tn(DatasetMode.CREATE_OVERWRITE), value: DatasetMode.CREATE_OVERWRITE },
  ];

  const conflictOptions = [
    { label: tn(DatasetConflict.USE_EXISTING_IF_SAME_NAME), value: DatasetConflict.USE_EXISTING_IF_SAME_NAME },
    { label: tn(DatasetConflict.OVERWRITE), value: DatasetConflict.OVERWRITE },
  ];

  return (
    <Stack spacing="md">
      <Stack spacing="md">
        <Text color="gray-900">{tn('dataset_mode')}</Text>
        <Select
          value={state.mode}
          dropdownMatchSelectWidth={false}
          onChange={(newValue: DatasetMode) => {
            setState({ mode: newValue });
          }}
          optionData={modeOptions}
        />
      </Stack>
      {state.mode === DatasetMode.CREATE_OVERWRITE && (
        <Stack spacing="md">
          <Text color="gray-900">{tn('dataset_conflict')}</Text>
          <Select
            value={state.conflict}
            dropdownMatchSelectWidth={false}
            onChange={(newValue: DatasetConflict) => {
              setState({ conflict: newValue });
            }}
            optionData={conflictOptions}
          />
        </Stack>
      )}
    </Stack>
  );
};

export default withI18n(DatasetOptions, 'DatasetOptions');
